import { useMemo } from "react"
import { Business, ConditionGroup, NextPageParams, PaginationMetadata } from "@/schema"
import { useDataTableStore } from "@/stores/data-table-store"
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import {
  AllBusinessIdsResponse,
  BusinessesResponse,
  fetchAllBusinessIds,
  fetchBusiness,
  fetchBusinessContacts,
  fetchBusinesses,
  fetchBusinessPeople,
  updateBusiness,
  UpdateBusinessPayload,
} from "./api"

export function useInfiniteBusinessPeople(businessId: string, search: string, includeFormer: boolean) {
  const getContext = useApiContext()
  return useInfiniteQuery({
    queryKey: ["business", businessId, "people", search, includeFormer],
    queryFn: async ({ pageParam }) =>
      fetchBusinessPeople(await getContext(), businessId, pageParam, search, includeFormer),
    initialPageParam: undefined as NextPageParams | undefined,
    getNextPageParam: (lastPage) => lastPage.pagination.next_page_params ?? undefined,
  })
}

export function useInfiniteBusinessContacts(businessId: string, search: string) {
  const getContext = useApiContext()
  return useInfiniteQuery({
    queryKey: ["business", businessId, "contacts", search],
    queryFn: async ({ pageParam }) => fetchBusinessContacts(await getContext(), businessId, pageParam, search),
    initialPageParam: undefined as NextPageParams | undefined,
    getNextPageParam: (lastPage) => lastPage.pagination.next_page_params ?? undefined,
  })
}

export function useFetchBusinesses(searchTerm?: string, bookIdOverride?: string) {
  const getContext = useApiContext(bookIdOverride)
  return useQuery({
    queryKey: ["businesses", searchTerm, bookIdOverride],
    queryFn: async () => {
      const content = await getContext()
      if (!content) throw new Error("No content found")
      return fetchBusinesses(content, undefined, undefined, searchTerm, undefined, true)
    },
  })
}

export function useInfiniteBusinesses(enabled?: boolean) {
  const getContext = useApiContext()
  const { searchTerm, completedConditions, viewSort } = useDataTableStore()
  const computedFilterGroup = useMemo(
    () =>
      ({
        combinator: "and",
        conditions: completedConditions,
      }) as ConditionGroup,
    [completedConditions]
  )
  return useInfiniteQuery<
    BusinessesResponse,
    Error,
    { businesses: Business[]; pagination: PaginationMetadata },
    (string | ConditionGroup | undefined | { field?: string; direction?: "asc" | "desc" })[],
    NextPageParams | undefined
  >({
    queryKey: ["businesses", "infinite", searchTerm, computedFilterGroup, viewSort],
    staleTime: 0,
    queryFn: async ({ pageParam }) => {
      const context = await getContext()
      if (!context) throw new Error("No content found")

      return fetchBusinesses(context, computedFilterGroup, pageParam, searchTerm, viewSort)
    },
    initialPageParam: undefined,
    enabled,
    getNextPageParam: (lastPage) => lastPage.pagination.next_page_params ?? undefined,
    select: (data) => ({
      businesses: data.pages.flatMap((page) => page.businesses),
      pagination: data.pages[data.pages.length - 1]?.pagination,
    }),
  })
}

export function useFetchBusiness(id: string | null) {
  const getContext = useApiContext()
  return useQuery({
    queryKey: ["business", id],
    enabled: !!id,
    queryFn: async () => {
      const content = await getContext()
      if (!content) throw new Error("No content found")
      if (!id) throw new Error("No id found")
      return fetchBusiness(content, id)
    },
  })
}

export function useFetchAllBusinessIds(enabled?: boolean) {
  const getContext = useApiContext()
  const { searchTerm, completedConditions, viewSort } = useDataTableStore()
  const computedFilterGroup = useMemo(
    () =>
      ({
        combinator: "and",
        conditions: completedConditions,
      }) as ConditionGroup,
    [completedConditions]
  )

  return useQuery<AllBusinessIdsResponse>({
    queryKey: ["businesses", "all_ids", searchTerm, computedFilterGroup, viewSort],
    queryFn: async () => {
      const content = await getContext()
      if (!content) throw new Error("No content found")
      return fetchAllBusinessIds(content, computedFilterGroup, searchTerm)
    },
    enabled,
  })
}

export function useUpdateBusiness(businessId: string) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: UpdateBusinessPayload) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return updateBusiness(context, businessId, params)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["business", businessId] })
      queryClient.invalidateQueries({ queryKey: ["businesses"] })
    },
  })
}
